import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FaChalkboardTeacher, FaPlus, FaTrash, FaBook,
  FaUsers, FaSpinner, FaClock, FaTimes, FaVideo,
} from 'react-icons/fa';
import { notifySuccess, notifyError } from '../../utils/notifications';
import { useAuth } from '../../context/AuthContext';
import { fetchTeacherCourses, createCourse, deleteTeacherCourse } from '../../services/courseService';
import usePagination from '../../hooks/usePagination';
import SEO from '../../components/common/SEO';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import Pagination from '../../components/common/Pagination';
import AdminEmptyState from '../Admin/components/AdminEmptyState';
import { AdminStat } from '../Admin/components/AdminListCard';
import DashboardShell from './DashboardShell';

const PAGE_SIZE = 6;

const LEVELS = ['Beginner', 'Intermediate', 'Advanced', 'Beginner to Advanced', 'All Levels'];

const emptyForm = { title: '', description: '', duration: '', level: '' };

const TeacherDashboard = () => {
  const navigate = useNavigate();
  const { user, isTeacher, isAuthenticated, getAuthHeaders } = useAuth();
  const [courses, setCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, courseId: null, courseTitle: '' });
  const pager = usePagination(courses, PAGE_SIZE);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/dashboard/teacher' } });
      return;
    }
    if (!isTeacher) {
      notifyError('Access denied. Teachers only.');
      navigate('/');
    }
  }, [isAuthenticated, isTeacher, navigate]);

  const loadCourses = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await fetchTeacherCourses(getAuthHeaders());
      setCourses(data);
    } catch (err) {
      notifyError(err.message || 'Failed to load courses');
    } finally {
      setIsLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (isTeacher) loadCourses();
  }, [isTeacher, loadCourses]);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) {
      notifyError('Title and description are required');
      return;
    }
    setIsSubmitting(true);
    try {
      const newCourse = await createCourse(form, getAuthHeaders());
      setCourses((prev) => [{ ...newCourse, enrollment_count: 0 }, ...prev]);
      setForm(emptyForm);
      setShowForm(false);
      pager.setPage(1);
      notifySuccess('Course created successfully!');
    } catch (err) {
      notifyError(err.message || 'Failed to create course');
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmDelete = (courseId, courseTitle) => {
    setDeleteDialog({ isOpen: true, courseId, courseTitle });
  };

  const handleDeleteConfirmed = async () => {
    const { courseId } = deleteDialog;
    setDeleteDialog({ isOpen: false, courseId: null, courseTitle: '' });
    setDeletingId(courseId);
    try {
      await deleteTeacherCourse(courseId, getAuthHeaders());
      setCourses((prev) => prev.filter((c) => c.id !== courseId));
      notifySuccess('Course deleted successfully.');
    } catch (err) {
      notifyError(err.message || 'Failed to delete course');
    } finally {
      setDeletingId(null);
    }
  };

  if (!isTeacher) return null;

  return (
    <DashboardShell
      role="teacher"
      title={`Welcome${user?.name ? `, ${user.name}` : ''}`}
      subtitle="Manage your courses and students"
      headerActions={(
        <>
          <button
            type="button"
            onClick={() => setShowForm((p) => !p)}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-designer-primary hover:bg-red-600 text-white text-sm font-semibold transition-colors shadow-sm"
          >
            <FaPlus className="w-3 h-3" />
            {showForm ? 'Close form' : 'New Course'}
          </button>
          <Link
            to="/account/password"
            className="inline-flex items-center px-4 py-2 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:text-designer-primary hover:border-designer-primary/40 transition-colors"
          >
            Change password
          </Link>
        </>
      )}
    >
      <SEO title="Teacher Dashboard | Arvix Academy" description="Manage your courses on Arvix Academy" />
      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={() => setDeleteDialog({ isOpen: false, courseId: null, courseTitle: '' })}
        onConfirm={handleDeleteConfirmed}
        title="Delete Course"
        message={`Are you sure you want to delete "${deleteDialog.courseTitle}"? This action cannot be undone and all enrollments will be removed.`}
        confirmText="Yes, Delete"
        cancelText="Cancel"
        type="danger"
      />

      <div className="space-y-6 animate-fadeIn">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <AdminStat icon={FaBook} label="Total Courses" value={courses.length} tile="bg-red-50 text-designer-primary" />
          <AdminStat
            icon={FaUsers}
            label="Total Students"
            value={courses.reduce((sum, c) => sum + (c.enrollment_count || 0), 0)}
            tile="bg-blue-50 text-blue-600"
          />
        </div>

        {/* Create Course Form */}
        {showForm && (
          <div className="bg-white rounded-xl border border-gray-200/80 shadow-sm p-5 sm:p-6 animate-fade-in">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-designer-dark flex items-center gap-2">
                <FaChalkboardTeacher className="text-designer-primary" />
                Create New Course
              </h2>
              <button
                type="button"
                onClick={() => { setShowForm(false); setForm(emptyForm); }}
                aria-label="Close form"
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <FaTimes className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Course Title <span className="text-designer-primary">*</span>
                  </label>
                  <input
                    name="title"
                    value={form.title}
                    onChange={handleChange}
                    placeholder="e.g. Advanced Photoshop Techniques"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-designer-primary/30 focus:border-designer-primary transition-all"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Description <span className="text-designer-primary">*</span>
                  </label>
                  <textarea
                    name="description"
                    value={form.description}
                    onChange={handleChange}
                    rows={3}
                    placeholder="What will students learn in this course?"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-designer-primary/30 focus:border-designer-primary transition-all resize-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Duration</label>
                  <div className="relative">
                    <FaClock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
                    <input
                      name="duration"
                      value={form.duration}
                      onChange={handleChange}
                      placeholder="e.g. 3 Months"
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-designer-primary/30 focus:border-designer-primary transition-all"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Level</label>
                  <select
                    name="level"
                    value={form.level}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-designer-primary/30 focus:border-designer-primary transition-all bg-white"
                  >
                    <option value="">Select level</option>
                    {LEVELS.map((l) => (
                      <option key={l} value={l}>{l}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex items-center gap-2 px-6 py-2.5 bg-designer-primary hover:bg-red-600 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-60"
                >
                  {isSubmitting ? <FaSpinner className="animate-spin w-4 h-4" /> : <FaPlus className="w-3.5 h-3.5" />}
                  {isSubmitting ? 'Creating...' : 'Create Course'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Courses List */}
        <div>
          <h2 className="text-lg font-bold text-designer-dark mb-4">Your Courses ({courses.length})</h2>
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <FaSpinner className="animate-spin text-designer-primary w-8 h-8" />
            </div>
          ) : courses.length === 0 ? (
            <AdminEmptyState
              icon={FaChalkboardTeacher}
              title="No courses yet"
              description='Click "New Course" to create your first course'
            />
          ) : (
            <>
              <div className="space-y-4" ref={pager.listRef}>
                {pager.items.map((course) => (
                  <article
                    key={course.id}
                    className="bg-white rounded-xl border border-gray-200/80 shadow-sm p-5 flex flex-col sm:flex-row sm:items-center gap-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                        {course.status === 'approved' && !course.is_hidden && (
                          <span className="text-[11px] px-2 py-0.5 bg-green-50 text-green-700 rounded-md font-semibold">Live</span>
                        )}
                        {course.status === 'pending' && (
                          <span className="text-[11px] px-2 py-0.5 bg-amber-50 text-amber-700 rounded-md font-semibold">Pending review</span>
                        )}
                        {course.status === 'rejected' && (
                          <span className="text-[11px] px-2 py-0.5 bg-red-50 text-red-600 rounded-md font-semibold">Rejected</span>
                        )}
                        {course.is_hidden && (
                          <span className="text-[11px] px-2 py-0.5 bg-gray-100 text-gray-600 rounded-md font-semibold">Hidden</span>
                        )}
                      </div>
                      <h3 className="font-bold text-designer-dark text-base truncate">{course.title}</h3>
                      <p className="text-gray-500 text-sm mt-1 line-clamp-2">{course.description}</p>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-2.5">
                        {course.duration && (
                          <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                            <FaClock className="text-designer-primary w-3 h-3" />
                            {course.duration}
                          </span>
                        )}
                        {course.level && (
                          <span className="text-[11px] px-2 py-0.5 bg-red-50 text-designer-primary rounded-md font-semibold">
                            {course.level}
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                          <FaUsers className="text-designer-primary w-3 h-3" />
                          {course.enrollment_count || 0} enrolled
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 shrink-0">
                      <Link
                        to={`/admin/studio/${course.slug || course.id}`}
                        className="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white bg-designer-primary hover:bg-red-600 rounded-lg transition-colors"
                      >
                        <FaVideo className="w-3.5 h-3.5" />
                        Manage classes
                      </Link>
                      {course.enrollment_mode !== 'managed' && (
                        <button
                          type="button"
                          onClick={() => confirmDelete(course.id, course.title)}
                          disabled={deletingId === course.id}
                          className="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold text-gray-400 border border-gray-200 rounded-lg hover:text-red-600 hover:border-red-200 hover:bg-red-50 transition-colors disabled:opacity-50"
                        >
                          {deletingId === course.id ? (
                            <FaSpinner className="animate-spin w-3.5 h-3.5" />
                          ) : (
                            <FaTrash className="w-3.5 h-3.5" />
                          )}
                          Delete
                        </button>
                      )}
                    </div>
                  </article>
                ))}
              </div>
              <Pagination currentPage={pager.page} totalPages={pager.totalPages} onPageChange={pager.changePage} />
            </>
          )}
        </div>
      </div>
    </DashboardShell>
  );
};

export default TeacherDashboard;
